/**
 * Intelligent LaTeX autocompletion provider for Monaco Editor.
 *
 * Features:
 *  • Command scaffolding: \frac → \frac{▮}{}, \begin → \begin{▮}\n\end{▮}
 *  • Environment name completion inside \begin{} and \end{}
 *  • Math symbol completions (\alpha, \beta, etc.)
 *  • Section / formatting command completions
 *  • Snippet-style placeholders with tab stops
 */

import type { languages, editor, Position, IRange } from "monaco-editor"
import { LATEX_LANGUAGE_ID } from "./monaco-latex"

/* ────────────────────────── Completion data ────────────────────────── */

/** Commands that expand into multi-argument scaffolds */
const SCAFFOLD_COMMANDS: Array<{
  label: string
  insertText: string
  detail: string
  documentation: string
}> = [
  {
    label: "\\frac",
    insertText: "\\frac{${1:numerator}}{${2:denominator}}",
    detail: "Fraction",
    documentation: "Insert a fraction: \\frac{num}{den}",
  },
  {
    label: "\\sqrt",
    insertText: "\\sqrt{${1:expression}}",
    detail: "Square root",
    documentation: "Insert a square root: \\sqrt{expr}",
  },
  {
    label: "\\sqrt[]",
    insertText: "\\sqrt[${1:n}]{${2:expression}}",
    detail: "Nth root",
    documentation: "Insert an nth root: \\sqrt[n]{expr}",
  },
  {
    label: "\\text",
    insertText: "\\text{${1:text}}",
    detail: "Text in math",
    documentation: "Insert text inside math mode",
  },
  {
    label: "\\textbf",
    insertText: "\\textbf{${1:text}}",
    detail: "Bold text",
    documentation: "Insert bold text: \\textbf{text}",
  },
  {
    label: "\\textit",
    insertText: "\\textit{${1:text}}",
    detail: "Italic text",
    documentation: "Insert italic text: \\textit{text}",
  },
  {
    label: "\\texttt",
    insertText: "\\texttt{${1:text}}",
    detail: "Monospace text",
    documentation: "Insert monospace text: \\texttt{text}",
  },
  {
    label: "\\emph",
    insertText: "\\emph{${1:text}}",
    detail: "Emphasis",
    documentation: "Insert emphasized text: \\emph{text}",
  },
  {
    label: "\\underline",
    insertText: "\\underline{${1:text}}",
    detail: "Underline",
    documentation: "Insert underlined text",
  },
  {
    label: "\\overline",
    insertText: "\\overline{${1:expression}}",
    detail: "Overline",
    documentation: "Insert an overline: \\overline{expr}",
  },
  {
    label: "\\hat",
    insertText: "\\hat{${1:x}}",
    detail: "Hat accent",
    documentation: "Insert hat: \\hat{x}",
  },
  {
    label: "\\bar",
    insertText: "\\bar{${1:x}}",
    detail: "Bar accent",
    documentation: "Insert bar: \\bar{x}",
  },
  {
    label: "\\tilde",
    insertText: "\\tilde{${1:x}}",
    detail: "Tilde accent",
    documentation: "Insert tilde: \\tilde{x}",
  },
  {
    label: "\\vec",
    insertText: "\\vec{${1:x}}",
    detail: "Vector accent",
    documentation: "Insert vector: \\vec{x}",
  },
  {
    label: "\\dot",
    insertText: "\\dot{${1:x}}",
    detail: "Dot accent",
    documentation: "Insert dot: \\dot{x}",
  },
  {
    label: "\\ddot",
    insertText: "\\ddot{${1:x}}",
    detail: "Double dot",
    documentation: "Insert double dot: \\ddot{x}",
  },
  {
    label: "\\sum",
    insertText: "\\sum_{${1:i=1}}^{${2:n}} ${3:expression}",
    detail: "Summation",
    documentation: "Insert summation with bounds",
  },
  {
    label: "\\prod",
    insertText: "\\prod_{${1:i=1}}^{${2:n}} ${3:expression}",
    detail: "Product",
    documentation: "Insert product with bounds",
  },
  {
    label: "\\int",
    insertText: "\\int_{${1:a}}^{${2:b}} ${3:f(x)}\\, d${4:x}",
    detail: "Integral",
    documentation: "Insert integral with bounds",
  },
  {
    label: "\\lim",
    insertText: "\\lim_{${1:x \\to \\infty}} ${2:expression}",
    detail: "Limit",
    documentation: "Insert limit",
  },
  {
    label: "\\binom",
    insertText: "\\binom{${1:n}}{${2:k}}",
    detail: "Binomial coefficient",
    documentation: "Insert binomial coefficient",
  },
  {
    label: "\\left(  \\right)",
    insertText: "\\left( ${1:expression} \\right)",
    detail: "Auto-sized parentheses",
    documentation: "Insert auto-sized parentheses",
  },
  {
    label: "\\left[  \\right]",
    insertText: "\\left[ ${1:expression} \\right]",
    detail: "Auto-sized brackets",
    documentation: "Insert auto-sized brackets",
  },
  {
    label: "\\left\\{  \\right\\}",
    insertText: "\\left\\{ ${1:expression} \\right\\}",
    detail: "Auto-sized braces",
    documentation: "Insert auto-sized braces",
  },
  {
    label: "\\mathrm",
    insertText: "\\mathrm{${1:text}}",
    detail: "Roman math text",
    documentation: "Insert upright text in math mode",
  },
  {
    label: "\\mathbf",
    insertText: "\\mathbf{${1:text}}",
    detail: "Bold math text",
    documentation: "Insert bold text in math mode",
  },
  {
    label: "\\mathcal",
    insertText: "\\mathcal{${1:text}}",
    detail: "Calligraphic",
    documentation: "Insert calligraphic text in math mode",
  },
  {
    label: "\\mathbb",
    insertText: "\\mathbb{${1:text}}",
    detail: "Blackboard bold",
    documentation: "Insert blackboard bold (e.g., ℝ, ℤ)",
  },
  {
    label: "\\label",
    insertText: "\\label{${1:key}}",
    detail: "Label",
    documentation: "Insert a label for cross-referencing",
  },
  {
    label: "\\ref",
    insertText: "\\ref{${1:key}}",
    detail: "Reference",
    documentation: "Insert a cross-reference",
  },
  {
    label: "\\cite",
    insertText: "\\cite{${1:key}}",
    detail: "Citation",
    documentation: "Insert a citation",
  },
  {
    label: "\\usepackage",
    insertText: "\\usepackage{${1:package}}",
    detail: "Use package",
    documentation: "Include a LaTeX package",
  },
  {
    label: "\\usepackage[]",
    insertText: "\\usepackage[${1:options}]{${2:package}}",
    detail: "Use package with options",
    documentation: "Include a LaTeX package with options",
  },
  {
    label: "\\documentclass",
    insertText: "\\documentclass{${1:article}}",
    detail: "Document class",
    documentation: "Set the document class",
  },
  {
    label: "\\documentclass[]",
    insertText: "\\documentclass[${1:options}]{${2:article}}",
    detail: "Document class with options",
    documentation: "Set the document class with options",
  },
  {
    label: "\\includegraphics",
    insertText: "\\includegraphics[width=${1:0.8}\\textwidth]{${2:image}}",
    detail: "Include graphics",
    documentation: "Insert an image",
  },
  {
    label: "\\href",
    insertText: "\\href{${1:url}}{${2:text}}",
    detail: "Hyperlink",
    documentation: "Insert a hyperlink",
  },
  {
    label: "\\footnote",
    insertText: "\\footnote{${1:text}}",
    detail: "Footnote",
    documentation: "Insert a footnote",
  },
  {
    label: "\\caption",
    insertText: "\\caption{${1:text}}",
    detail: "Caption",
    documentation: "Insert a caption",
  },
  {
    label: "\\item",
    insertText: "\\item ${1:text}",
    detail: "List item",
    documentation: "Insert a list item",
  },
]

/** Environment names for \begin / \end completion */
const ENVIRONMENT_NAMES = [
  { name: "document", detail: "Document body" },
  { name: "equation", detail: "Numbered equation" },
  { name: "equation*", detail: "Unnumbered equation" },
  { name: "align", detail: "Aligned equations (numbered)" },
  { name: "align*", detail: "Aligned equations (unnumbered)" },
  { name: "gather", detail: "Gathered equations" },
  { name: "multline", detail: "Multi-line equation" },
  { name: "figure", detail: "Figure float" },
  { name: "table", detail: "Table float" },
  { name: "tabular", detail: "Tabular data" },
  { name: "itemize", detail: "Bullet list" },
  { name: "enumerate", detail: "Numbered list" },
  { name: "description", detail: "Description list" },
  { name: "center", detail: "Centered content" },
  { name: "abstract", detail: "Abstract" },
  { name: "quote", detail: "Block quote" },
  { name: "verbatim", detail: "Verbatim text" },
  { name: "lstlisting", detail: "Code listing" },
  { name: "minipage", detail: "Minipage" },
  { name: "tikzpicture", detail: "TikZ picture" },
  { name: "frame", detail: "Beamer frame" },
  { name: "theorem", detail: "Theorem" },
  { name: "proof", detail: "Proof" },
  { name: "definition", detail: "Definition" },
  { name: "lemma", detail: "Lemma" },
  { name: "corollary", detail: "Corollary" },
  { name: "proposition", detail: "Proposition" },
  { name: "remark", detail: "Remark" },
  { name: "example", detail: "Example" },
  { name: "cases", detail: "Cases (math)" },
  { name: "matrix", detail: "Matrix" },
  { name: "pmatrix", detail: "Matrix with parentheses" },
  { name: "bmatrix", detail: "Matrix with brackets" },
  { name: "vmatrix", detail: "Determinant matrix" },
  { name: "array", detail: "Array" },
  { name: "split", detail: "Split equation" },
  { name: "flushright", detail: "Right-aligned" },
  { name: "flushleft", detail: "Left-aligned" },
]

/** Greek letters and common math symbols */
const MATH_SYMBOLS: Array<{ label: string; detail: string }> = [
  // Greek lowercase
  { label: "\\alpha", detail: "α" },
  { label: "\\beta", detail: "β" },
  { label: "\\gamma", detail: "γ" },
  { label: "\\delta", detail: "δ" },
  { label: "\\epsilon", detail: "ε" },
  { label: "\\varepsilon", detail: "ε (variant)" },
  { label: "\\zeta", detail: "ζ" },
  { label: "\\eta", detail: "η" },
  { label: "\\theta", detail: "θ" },
  { label: "\\vartheta", detail: "ϑ (variant)" },
  { label: "\\iota", detail: "ι" },
  { label: "\\kappa", detail: "κ" },
  { label: "\\lambda", detail: "λ" },
  { label: "\\mu", detail: "μ" },
  { label: "\\nu", detail: "ν" },
  { label: "\\xi", detail: "ξ" },
  { label: "\\pi", detail: "π" },
  { label: "\\rho", detail: "ρ" },
  { label: "\\sigma", detail: "σ" },
  { label: "\\tau", detail: "τ" },
  { label: "\\upsilon", detail: "υ" },
  { label: "\\phi", detail: "φ" },
  { label: "\\varphi", detail: "ϕ (variant)" },
  { label: "\\chi", detail: "χ" },
  { label: "\\psi", detail: "ψ" },
  { label: "\\omega", detail: "ω" },
  // Greek uppercase
  { label: "\\Gamma", detail: "Γ" },
  { label: "\\Delta", detail: "Δ" },
  { label: "\\Theta", detail: "Θ" },
  { label: "\\Lambda", detail: "Λ" },
  { label: "\\Xi", detail: "Ξ" },
  { label: "\\Pi", detail: "Π" },
  { label: "\\Sigma", detail: "Σ" },
  { label: "\\Phi", detail: "Φ" },
  { label: "\\Psi", detail: "Ψ" },
  { label: "\\Omega", detail: "Ω" },
  // Operators
  { label: "\\infty", detail: "∞" },
  { label: "\\partial", detail: "∂" },
  { label: "\\nabla", detail: "∇" },
  { label: "\\forall", detail: "∀" },
  { label: "\\exists", detail: "∃" },
  { label: "\\neg", detail: "¬" },
  { label: "\\in", detail: "∈" },
  { label: "\\notin", detail: "∉" },
  { label: "\\subset", detail: "⊂" },
  { label: "\\subseteq", detail: "⊆" },
  { label: "\\supset", detail: "⊃" },
  { label: "\\supseteq", detail: "⊇" },
  { label: "\\cup", detail: "∪" },
  { label: "\\cap", detail: "∩" },
  { label: "\\emptyset", detail: "∅" },
  { label: "\\times", detail: "×" },
  { label: "\\cdot", detail: "·" },
  { label: "\\circ", detail: "∘" },
  { label: "\\pm", detail: "±" },
  { label: "\\mp", detail: "∓" },
  { label: "\\leq", detail: "≤" },
  { label: "\\geq", detail: "≥" },
  { label: "\\neq", detail: "≠" },
  { label: "\\approx", detail: "≈" },
  { label: "\\equiv", detail: "≡" },
  { label: "\\sim", detail: "∼" },
  { label: "\\propto", detail: "∝" },
  { label: "\\to", detail: "→" },
  { label: "\\rightarrow", detail: "→" },
  { label: "\\leftarrow", detail: "←" },
  { label: "\\Rightarrow", detail: "⇒" },
  { label: "\\Leftarrow", detail: "⇐" },
  { label: "\\Leftrightarrow", detail: "⇔" },
  { label: "\\iff", detail: "⟺" },
  { label: "\\implies", detail: "⟹" },
  { label: "\\mapsto", detail: "↦" },
  { label: "\\ldots", detail: "…" },
  { label: "\\cdots", detail: "⋯" },
  { label: "\\vdots", detail: "⋮" },
  { label: "\\ddots", detail: "⋱" },
  // Functions
  { label: "\\sin", detail: "sin" },
  { label: "\\cos", detail: "cos" },
  { label: "\\tan", detail: "tan" },
  { label: "\\log", detail: "log" },
  { label: "\\ln", detail: "ln" },
  { label: "\\exp", detail: "exp" },
  { label: "\\min", detail: "min" },
  { label: "\\max", detail: "max" },
  { label: "\\sup", detail: "sup" },
  { label: "\\inf", detail: "inf" },
  { label: "\\det", detail: "det" },
  { label: "\\dim", detail: "dim" },
  { label: "\\ker", detail: "ker" },
  { label: "\\hom", detail: "Hom" },
  { label: "\\arg", detail: "arg" },
  { label: "\\deg", detail: "deg" },
  { label: "\\gcd", detail: "gcd" },
]

/** Section commands */
const SECTION_COMMANDS: Array<{ label: string; insertText: string; detail: string }> = [
  { label: "\\part", insertText: "\\part{${1:title}}", detail: "Part" },
  { label: "\\chapter", insertText: "\\chapter{${1:title}}", detail: "Chapter" },
  { label: "\\section", insertText: "\\section{${1:title}}", detail: "Section" },
  { label: "\\subsection", insertText: "\\subsection{${1:title}}", detail: "Subsection" },
  { label: "\\subsubsection", insertText: "\\subsubsection{${1:title}}", detail: "Subsubsection" },
  { label: "\\paragraph", insertText: "\\paragraph{${1:title}}", detail: "Paragraph" },
  { label: "\\subparagraph", insertText: "\\subparagraph{${1:title}}", detail: "Subparagraph" },
]

/* ────────────────────────── Provider ────────────────────────── */

/**
 * Register the LaTeX completion provider with Monaco.
 * Call once during language registration.
 */
export function registerLatexCompletionProvider(
  monaco: typeof import("monaco-editor"),
): void {
  monaco.languages.registerCompletionItemProvider(LATEX_LANGUAGE_ID, {
    triggerCharacters: ["\\", "{"],

    provideCompletionItems(
      model: editor.ITextModel,
      position: Position,
    ): languages.ProviderResult<languages.CompletionList> {
      const lineContent = model.getLineContent(position.lineNumber)
      const textUntilPosition = lineContent.substring(0, position.column - 1)

      const wordInfo = model.getWordUntilPosition(position)
      const range: IRange = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      }

      const suggestions: languages.CompletionItem[] = []

      // ─── Case 1: Inside \begin{…} or \end{…} — complete environment names ───
      const envMatch = textUntilPosition.match(/\\(?:begin|end)\{([a-zA-Z*]*)$/)
      if (envMatch) {
        const prefix = envMatch[1].toLowerCase()
        const envRange: IRange = {
          startLineNumber: position.lineNumber,
          startColumn: position.column - prefix.length,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        }

        for (const env of ENVIRONMENT_NAMES) {
          if (env.name.toLowerCase().startsWith(prefix) || prefix === "") {
            const isBegin = textUntilPosition.includes("\\begin{")

            // For \begin, auto-insert the matching \end
            if (isBegin) {
              suggestions.push({
                label: env.name,
                kind: monaco.languages.CompletionItemKind.Module,
                detail: env.detail,
                documentation: `Insert \\begin{${env.name}}...\\end{${env.name}}`,
                insertText: `${env.name}}\n\t$0\n\\\\end{${env.name}}`,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range: envRange,
                sortText: `0-${env.name}`,
              })
            } else {
              suggestions.push({
                label: env.name,
                kind: monaco.languages.CompletionItemKind.Module,
                detail: env.detail,
                insertText: env.name,
                range: envRange,
                sortText: `0-${env.name}`,
              })
            }
          }
        }
        return { suggestions }
      }

      // ─── Case 2: After a backslash — complete commands ───
      const cmdMatch = textUntilPosition.match(/\\([a-zA-Z]*)$/)
      if (cmdMatch) {
        const prefix = cmdMatch[1].toLowerCase()
        const cmdRange: IRange = {
          startLineNumber: position.lineNumber,
          startColumn: position.column - prefix.length - 1, // include the backslash
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        }

        // \begin scaffold
        if ("begin".startsWith(prefix)) {
          suggestions.push({
            label: "\\begin{…}  \\end{…}",
            kind: monaco.languages.CompletionItemKind.Snippet,
            detail: "Environment block",
            documentation: "Insert a \\begin/\\end environment pair",
            insertText: "\\begin{${1:environment}}\n\t$0\n\\end{${1:environment}}",
            insertTextRules:
              monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range: cmdRange,
            sortText: "0-begin",
          })
        }

        // Scaffold commands (\frac, \sqrt, \int, etc.)
        for (const cmd of SCAFFOLD_COMMANDS) {
          const cmdName = cmd.label.replace(/^\\/, "").replace(/\[.*/, "").toLowerCase()
          if (cmdName.startsWith(prefix) || prefix === "") {
            suggestions.push({
              label: cmd.label,
              kind: monaco.languages.CompletionItemKind.Snippet,
              detail: cmd.detail,
              documentation: cmd.documentation,
              insertText: cmd.insertText,
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range: cmdRange,
              sortText: `1-${cmdName}`,
            })
          }
        }

        // Section commands
        for (const sec of SECTION_COMMANDS) {
          const secName = sec.label.replace(/^\\/, "").toLowerCase()
          if (secName.startsWith(prefix)) {
            suggestions.push({
              label: sec.label,
              kind: monaco.languages.CompletionItemKind.Snippet,
              detail: sec.detail,
              insertText: sec.insertText,
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range: cmdRange,
              sortText: `2-${secName}`,
            })
          }
        }

        // Math symbols
        for (const sym of MATH_SYMBOLS) {
          const symName = sym.label.replace(/^\\/, "").toLowerCase()
          if (symName.startsWith(prefix)) {
            suggestions.push({
              label: sym.label,
              kind: monaco.languages.CompletionItemKind.Constant,
              detail: sym.detail,
              insertText: sym.label,
              range: cmdRange,
              sortText: `3-${symName}`,
            })
          }
        }

        return { suggestions }
      }

      return { suggestions }
    },
  })
}
